import posthog, { CaptureResult } from 'posthog-js'

const POSTHOG_HOST = 'https://us.i.posthog.com'
// Project tokens are public by design (same as the Firebase web config).
const PROD_POSTHOG_TOKEN = 'phc_pN9Uw6bRhRiVEGcwJ8GTTUoPckfHCKuf3H4dujQEj2eo'

// Dev builds stay silent unless a dev project token is supplied via env.
const posthogToken =
  process.env.REACT_APP_POSTHOG_KEY ||
  (process.env.REACT_APP_ENVIRONMENT === 'production' ? PROD_POSTHOG_TOKEN : undefined)
const posthogHost = process.env.REACT_APP_POSTHOG_HOST || POSTHOG_HOST

export const isPostHogEnabled = Boolean(posthogToken)

const EMAIL_PATTERN = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi
const JWT_PATTERN = /eyJ[\w-]+\.[\w-]+\.[\w-]+/g
const AUTH_TOKEN_PARAM_PATTERN = /([?&](?:idToken|authToken)=)[^&#]*/gi
const AUTH_TOKEN_PARAM_TEST = /[?&](?:idToken|authToken)=/i

const redactSensitiveText = (text: string) =>
  text
    .replace(AUTH_TOKEN_PARAM_PATTERN, '$1[token]')
    .replace(EMAIL_PATTERN, '[email]')
    .replace(JWT_PATTERN, '[token]')

const hasAuthTokenInUrl = () => AUTH_TOKEN_PARAM_TEST.test(window.location.search)

function redactUrlValues(properties: Record<string, unknown> | undefined) {
  if (!properties) return properties
  return Object.fromEntries(
    Object.entries(properties).map(([key, value]) =>
      typeof value === 'string' && /url|referrer|pathname/i.test(key)
        ? [key, redactSensitiveText(value)]
        : [key, value]
    )
  )
}

// SSO links carry idToken/authToken in the query string; scrub URL-valued properties,
// including the $initial_* person properties, on every event.
function redactUrlProperties(event: CaptureResult): CaptureResult {
  return {
    ...event,
    properties: redactUrlValues(event.properties) ?? {},
    ...(event.$set ? { $set: redactUrlValues(event.$set) } : {}),
    ...(event.$set_once ? { $set_once: redactUrlValues(event.$set_once) } : {}),
  }
}

// Error messages can embed emails or Firebase ID tokens; scrub them before they leave the browser.
function redactExceptionMessages(event: CaptureResult): CaptureResult {
  const exceptions = event.properties?.$exception_list
  if (event.event !== '$exception' || !Array.isArray(exceptions)) return event

  return {
    ...event,
    properties: {
      ...event.properties,
      $exception_list: exceptions.map((exception) =>
        typeof exception?.value === 'string'
          ? { ...exception, value: redactSensitiveText(exception.value) }
          : exception
      ),
    },
  }
}

// Replays record the page URL, so recording waits until the auth guard strips the tokens.
const deferSessionRecording = isPostHogEnabled && hasAuthTokenInUrl()

if (posthogToken) {
  posthog.init(posthogToken, {
    api_host: posthogHost,
    defaults: '2026-05-30',
    // Console output can carry tokens and account details; keep it out of replays.
    enable_recording_console_log: false,
    disable_session_recording: deferSessionRecording,
    session_recording: {
      maskAllInputs: true,
      // Rendered PII (emails, names, phone numbers) opts in with className="ph-mask".
      maskTextSelector: '.ph-mask',
    },
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false,
    },
    before_send: [redactUrlProperties, redactExceptionMessages],
  })
}

let sessionRecordingResumed = false

export function resumeDeferredSessionRecording() {
  if (!deferSessionRecording || sessionRecordingResumed || hasAuthTokenInUrl()) return
  sessionRecordingResumed = true
  posthog.startSessionRecording()
}

export function captureEvent(event: string, properties?: Record<string, unknown>) {
  if (!isPostHogEnabled) return
  posthog.capture(event, properties)
}

export default posthog
